import { Input as ChakraInput, InputGroup, InputRightElement } from "@chakra-ui/react";

import { getColorWithOpacity } from "src/apps/config/colors";
import styled from "styled-components";
import { Text } from "../typography/Texts";
import { Icon, ICON_SIZES } from "../icon/Icon";
import { FadeInAnimation } from "../fadeIn/FadeInAnimation";
import { useTheme } from "src/apps/providers/ThemeStore";
import { colors } from "@permisso/permisso-components";

interface InputProps {
  value: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  placeholder?: string;
  error?: string | null;
  rightIconSVGPath?: JSX.Element;
  title?: string;
  disabled?: boolean;
  blocked?: boolean;
  dataTestId?: string;
}

export const Input = ({
  value,
  onChange,
  placeholder = "Placeholder",
  error,
  rightIconSVGPath,
  title,
  disabled = false,
  blocked = false,
  dataTestId,
}: InputProps) => {
  const { primaryColor, primaryButtonBackgroundColor, blackColor, errorColor } = useTheme();
  const focusStyle = {
    borderColor: primaryButtonBackgroundColor ?? primaryColor,
  };
  const errorFocusStyle = {
    borderColor: getColorWithOpacity(errorColor, 0.8),
    boxShadow: `0 0 0 .0625rem ${getColorWithOpacity(errorColor, 0.8)} !important`,
  };

  const focus = error ? errorFocusStyle : focusStyle;

  const blockedProps = blocked
    ? {
        background: colors.grey300,
        disabled: true,
        _hover: {},
        _disabled: {},
        cursor: "not-allowed",
      }
    : {};

  return (
    <InputContainer>
      {title ? <Title>{title}</Title> : null}
      <InputGroup>
        <ChakraInput
          data-testid={dataTestId}
          errorBorderColor={errorColor}
          borderColor={colors.grey400}
          focusBorderColor={colors.grey500}
          placeholder={placeholder}
          onChange={onChange}
          _focus={focus}
          value={value}
          color={blackColor}
          size={"md"}
          isInvalid={!!error}
          variant={"outline"}
          type={"text"}
          _placeholder={{ color: colors.grey500 }}
          disabled={disabled}
          {...blockedProps}
        />
        {rightIconSVGPath ? (
          <InputRightElement pointerEvents="none" style={{ paddingTop: ".25rem" }}>
            <Icon
              size={ICON_SIZES.s16}
              color={blackColor}
              path={rightIconSVGPath}
              opacity={disabled ? 0.5 : 1}
            />
          </InputRightElement>
        ) : null}
      </InputGroup>
      {error ? (
        <FadeInAnimation duration={0.4}>
          <ErrorTextContainer>
            <Text $color={errorColor}>{error}</Text>
          </ErrorTextContainer>
        </FadeInAnimation>
      ) : null}
    </InputContainer>
  );
};

const InputContainer = styled.div``;

const Title = styled(Text)`
  padding-bottom: 0.25rem;
`;

const ErrorTextContainer = styled.div`
  display: flex;
  flex: 1;
`;
